import IconBin from "@shared/components/icons/Bin"
import IconCopy from "@shared/components/icons/Copy"
import IconCopyPaste from "@shared/components/icons/CopyPaste"
import IconGoogleLogo from "@shared/components/icons/GoogleLogo"
import {
  DrilldownMenu,
  DrilldownRow,
  HeaderWithBackArrow,
  MenuHeader,
  MenuRoot,
  MenuTitle,
  Row,
  RowContent,
  RowIcon,
  RowLabel,
  Rows,
  ToggleCheck,
  ToggleRow,
} from "@shared/components/menus/DrilldownMenu"
import { FLAT_MENU_ROWS_CLASS } from "@shared/components/menus/drilldown-menu-classes"
import { ConfirmView } from "@shared/components/menus/views/ConfirmView"
import { useNotifier } from "@shared/components/providers/notifier-context"
import type { GoogleClassroomPostType } from "@shared/models/card-stack"
import { getCardId, type CardAttrsBase } from "@shared/unified-tiptap/util/card"
import { tracked } from "@supergrain/kernel/react"
import { GapCursor } from "@tiptap/pm/gapcursor"
import { Fragment, Slice, type Node as PMNode } from "@tiptap/pm/model"
import type { ReactNodeViewProps } from "@tiptap/react"
import { Globe02 } from "@untitledui/icons"
import { AnimatePresence } from "motion/react"
import type { ReactNode } from "react"
import { reportOperation } from "../../analytics/operation-event"
import { OPERATION_CONTRACTS } from "../../analytics/operations"
import { asStandardId } from "../../analytics/types"
import {
  GOOGLE_CLASSROOM_POST_TYPES,
  googleClassroomCardFromTitledCard,
} from "../../extensions/card-google-classroom/card-google-classroom-extension"
import type { ExtensionStandardAttrs } from "../../extensions/standard/standard-extension"
import { useUiContext } from "../../unified-tiptap-ui-context"
import { getCardClipboard, setCardClipboard } from "../../util/clipboard"
import { CardPublicCallout } from "./CardPublicCallout"

// In its own file (with only component exports) so that edits hot-reload
// via React Fast Refresh instead of reloading the page.

/**
 * A card's drilldown menu. Shared by every card node view: `getPos()` must point at the
 * card node itself, and the card supplies `pasteCard`.
 *
 * Trigger-agnostic: pass the trigger button as `children` (it becomes the menu's
 * pressable trigger element).
 */
export const CardMenu = tracked(
  (
    props: ReactNodeViewProps & {
      children: ReactNode
      onOpenChange?: (isOpen: boolean) => void
      /** Replace this card with a copied card of the same type. */
      pasteCard: (copiedCard: PMNode) => void
      canPostToGoogleClassroom?: boolean
    },
  ) => {
    const { node, getPos, editor, children, onOpenChange, pasteCard, canPostToGoogleClassroom } =
      props
    const attrs = node.attrs as CardAttrsBase
    const { openGoogleClassroomSidebar, studentLessonUrl } = useUiContext()
    const notify = useNotifier()

    const currentCardAtPosition = () => {
      const pos = getPos()
      if (pos === undefined) return null
      const currentNode = editor.state.doc.nodeAt(pos)
      if (currentNode?.type !== node.type) return null

      const originalId = getCardId(node)
      if (originalId !== null && getCardId(currentNode) !== originalId) return null
      return { pos, node: currentNode }
    }

    const doCopy = async () => {
      if (getPos() === undefined) return
      await setCardClipboard(editor, "card", node)
      const cardId = getCardId(node)
      if (cardId) {
        reportOperation(editor, OPERATION_CONTRACTS.OP_COPY_CARD, "card-menu", { cardId })
      }

      notify("success", "Card Copied!", "Use another card's menu to paste.")
    }

    const doPaste = () => {
      const cardClipboard = getCardClipboard(editor)
      if (cardClipboard === null) {
        notify("warning", "No card to paste!", "Use another card's dropdown menu to copy first.")
        return
      }

      if (cardClipboard.type === "standard") {
        // Append the standard to the card.
        const currentCard = currentCardAtPosition()
        if (currentCard === null) return
        if (!["titledCard", "untitledCard"].includes(currentCard.node.type.name)) {
          notify(
            "warning",
            "Cannot paste here!",
            "The copied standard cannot be pasted onto this card.",
          )
          return
        }

        const cardId = getCardId(currentCard.node)
        const standard = cardClipboard.node
        const standardId = (standard.attrs as ExtensionStandardAttrs).standard.id
        editor
          .chain()
          .label(OPERATION_CONTRACTS.OP_EDIT_STANDARDS, "card-menu", {
            cardId,
            addedIds: [asStandardId(standardId)],
            removedIds: [],
          })
          .command(({ tr }) => {
            // The card's last inner position, so the standard lands at its bottom.
            const addPos = currentCard.pos + currentCard.node.nodeSize - 1
            const cardEndBlock = currentCard.node.lastChild

            // If the card's last block is an empty P (e.g. the initial placeholder), replace it.
            let standardPos: number
            if (
              cardEndBlock &&
              cardEndBlock.type.name === "paragraph" &&
              cardEndBlock.content.size === 0
            ) {
              standardPos = addPos - cardEndBlock.nodeSize
              tr.replaceRange(standardPos, addPos, new Slice(Fragment.from(standard), 0, 0))
            } else {
              standardPos = addPos
              tr.insert(standardPos, standard)
            }
            tr.setSelection(new GapCursor(tr.doc.resolve(standardPos + standard.nodeSize)))
            return true
          })
          .focus()
          .run()
      } else {
        // Type is "card". Call pasteCard.
        // Except, a card can only be pasted onto a card of the same type.
        if (cardClipboard.node.type.name !== node.type.name) {
          const cardTypeDescs: Record<string, string> = {
            titledCard: "a lesson",
            untitledCard: "a note",
            cardGoogleClassroom: "a Google Classroom",
          }
          notify(
            "warning",
            "Cannot paste here!",
            `The copied card can only be pasted onto ${cardTypeDescs[cardClipboard.node.type.name]} card.`,
          )
          return
        }
        pasteCard(cardClipboard.node)
      }
    }

    const confirmDelete = () => {
      const currentCard = currentCardAtPosition()
      if (currentCard === null) return

      const cardId = getCardId(currentCard.node)
      const chain = editor.chain()
      if (cardId) chain.label(OPERATION_CONTRACTS.OP_DELETE_CARD, "card-menu", { cardId })
      chain
        .command(({ tr }) => {
          tr.deleteRange(currentCard.pos, currentCard.pos + currentCard.node.nodeSize)
          return true
        })
        .run()
    }

    const setIsPublic = (isPublic: boolean) => {
      const pos = getPos()
      if (pos === undefined) return

      const cardId = getCardId(node)
      const chain = editor.chain()
      if (cardId) {
        chain.label(OPERATION_CONTRACTS.OP_TOGGLE_CARD_PUBLIC, "card-menu", { cardId, isPublic })
      }
      chain
        .command(({ tr }) => {
          tr.setNodeAttribute(pos, "isPublic", isPublic)
          return true
        })
        .run()
    }

    /**
     * Replaces this card with the Google Classroom post it describes, then opens the sidebar
     * where such a card is edited (and where the teacher picks the classes to post to). The
     * card keeps its id, so its comments and any existing post follow it across.
     */
    const postToGoogleClassroom = (postType: GoogleClassroomPostType) => {
      const currentCard = currentCardAtPosition()
      if (currentCard === null) return

      const gcCard = googleClassroomCardFromTitledCard(editor, currentCard.node, postType)
      const cardId = getCardId(currentCard.node)
      const chain = editor.chain()
      if (cardId)
        chain.label(OPERATION_CONTRACTS.OP_POST_CARD_TO_GOOGLE_CLASSROOM, "card-menu", {
          cardId,
          postType,
        })
      chain
        .command(({ tr }) => {
          tr.replaceWith(currentCard.pos, currentCard.pos + currentCard.node.nodeSize, gcCard)
          return true
        })
        .run()

      // Open the GC sidebar for the new post.
      // Not reported as a menu open: the conversion above is already the user's one intent.
      openGoogleClassroomSidebar?.((gcCard.attrs as CardAttrsBase).id)
    }

    return (
      <DrilldownMenu
        ariaLabel="Card menu"
        // Narrower than the menus with sections and long template names: the widest thing this
        // one holds is the "Which type of post?" title, and 288px leaves it the same air on the
        // right that the header gives it on the left.
        className="w-72"
        trigger={children}
        header={
          <MenuHeader className="border-b border-border-secondary">
            <MenuTitle>Card Menu</MenuTitle>
          </MenuHeader>
        }
        onOpenChange={(isOpen) => {
          onOpenChange?.(isOpen)
          // Restore focus to the editor when you close the menu — without
          // scrollIntoView: the selection can be anywhere in the document (untouched,
          // it sits at the very end), and scrolling it into view on dismiss yanks the
          // card out from under the pointer.
          if (!isOpen) editor.commands.focus(null, { scrollIntoView: false })
        }}
      >
        {({ close }) => (
          <MenuRoot>
            <Rows className={FLAT_MENU_ROWS_CLASS}>
              <Row
                onAction={() => {
                  close()
                  doCopy()
                }}
                typeaheadText="Copy Card"
              >
                <RowContent>
                  <RowIcon icon={IconCopy} />
                  <RowLabel>Copy Card</RowLabel>
                </RowContent>
              </Row>
              {editor.isEditable && (
                <Row
                  onAction={() => {
                    close()
                    doPaste()
                  }}
                  typeaheadText="Paste Card"
                >
                  <RowContent>
                    <RowIcon icon={IconCopyPaste} />
                    <RowLabel>Paste Card</RowLabel>
                  </RowContent>
                </Row>
              )}

              {editor.isEditable ? (
                <>
                  <DrilldownRow
                    typeaheadText="Delete Card"
                    header={({ back }) => (
                      <HeaderWithBackArrow backTo="Card Menu" onBack={back}>
                        Delete Card
                      </HeaderWithBackArrow>
                    )}
                    body={({ back }) => (
                      <ConfirmView
                        body="Are you sure you want to delete this card?"
                        confirmLabel="Delete"
                        destructive
                        onCancel={back}
                        onConfirm={() => {
                          close()
                          confirmDelete()
                        }}
                      />
                    )}
                  >
                    <RowContent>
                      <RowIcon icon={IconBin} />
                      <RowLabel>Delete Card</RowLabel>
                    </RowContent>
                  </DrilldownRow>

                  <ToggleRow
                    isSelected={attrs.isPublic ?? false}
                    onChange={setIsPublic}
                    typeaheadText="Show Card on Class Website"
                  >
                    <RowContent>
                      <RowIcon icon={Globe02} />
                      <RowLabel>Show Card on Class Website</RowLabel>
                      <ToggleCheck />
                    </RowContent>
                  </ToggleRow>
                  <AnimatePresence initial={false}>
                    {attrs.isPublic ? (
                      <CardPublicCallout studentLessonUrl={studentLessonUrl} />
                    ) : null}
                  </AnimatePresence>

                  {canPostToGoogleClassroom ? (
                    <DrilldownRow
                      typeaheadText="Post Card to Google Classroom"
                      header={({ back }) => (
                        <HeaderWithBackArrow backTo="Card Menu" onBack={back}>
                          Which type of post?
                        </HeaderWithBackArrow>
                      )}
                      body={() => (
                        // The rows' own inset is the header's, so the labels start at the back
                        // arrow's left edge while each row still takes the press full width.
                        <Rows className="pb-4 [--menu-row-inset:1.25rem]">
                          {GOOGLE_CLASSROOM_POST_TYPES.map(({ postType, label }) => (
                            <Row
                              key={postType}
                              onAction={() => {
                                close()
                                postToGoogleClassroom(postType)
                              }}
                            >
                              {label}
                            </Row>
                          ))}
                        </Rows>
                      )}
                    >
                      <RowContent>
                        <RowIcon icon={IconGoogleLogo} />
                        <RowLabel>Post Card to Google Classroom</RowLabel>
                      </RowContent>
                    </DrilldownRow>
                  ) : null}
                </>
              ) : null}
            </Rows>
          </MenuRoot>
        )}
      </DrilldownMenu>
    )
  },
)
